import { type LucideIcon } from "lucide-react";
import { Link } from "wouter";
import { cn } from "../../lib/utils";
import { StatusDot } from "./status-dot";

interface Props {
  label: string;
  value: number | string;
  caption?: string;
  Icon?: LucideIcon;
  tone?: "primary" | "warm" | "warn" | "danger" | "neutral";
  pulse?: boolean;
  href?: string;
  className?: string;
}

const TONE_BAR = {
  primary: "bg-primary",
  warm: "bg-warm",
  warn: "bg-warning",
  danger: "bg-destructive",
  neutral: "bg-border-strong",
};

const TONE_DOT = {
  primary: "ok" as const,
  warm: "warn" as const,
  warn: "warn" as const,
  danger: "danger" as const,
  neutral: "neutral" as const,
};

export function MetricCard({ label, value, caption, Icon, tone = "neutral", pulse, href, className }: Props) {
  const numeric = typeof value === "number" ? value : Number(value);
  const isZero = !Number.isNaN(numeric) && numeric === 0;

  const cls = cn(
    "group relative block overflow-hidden rounded-lg border border-border bg-card transition-all hover:border-border-strong hover:shadow-card",
    href && "cursor-pointer",
    className,
  );

  const body = (
    <>
      {/* left rail */}
      <span className={cn("absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full", TONE_BAR[tone])} aria-hidden />

      <div className="p-4 pl-5">
        <div className="flex items-center justify-between gap-2">
          <p className="font-mono text-[12px] uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
          {Icon ? (
            <Icon className="h-3.5 w-3.5 text-muted-foreground/70 transition-colors group-hover:text-primary" strokeWidth={1.75} />
          ) : (
            pulse && tone !== "neutral" && <StatusDot tone={TONE_DOT[tone]} pulse />
          )}
        </div>
        <div className="mt-2 flex items-baseline gap-2">
          <p
            className={cn(
              "numeric font-display font-medium leading-none tracking-tight",
              isZero ? "text-3xl text-muted-foreground/60" : "text-[34px] text-foreground",
            )}
          >
            {value}
          </p>
        </div>
        {caption && <p className="mt-1.5 text-[12px] text-muted-foreground">{caption}</p>}
      </div>
    </>
  );

  if (href) {
    return (
      <Link href={href} className={cls}>
        {body}
      </Link>
    );
  }
  return <div className={cls}>{body}</div>;
}
